/**
 * MedicalBookingModal
 * ───────────────────
 * Three-step appointment booking for a Medical practice:
 *   1. Doctor pick   — "Walk-In" (default) + practice-specific doctors
 *   2. Date + time   — drawn from /api/directory/medical/{slug}/availability
 *                      (server-deterministic 14-day rolling availability,
 *                       narrowed when a specific doctor is picked)
 *   3. Patient form  — name / phone / email / reason → POST /appointment
 *
 * "Schedule via Calendly" appears when the practice has `calendly_url` set
 * (admin-configurable). Otherwise the inline calendar above is the booking
 * surface. Both code paths converge on the same POST endpoint.
 */
import { useEffect, useMemo, useState } from "react";
import { PopupModal } from "react-calendly";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar as CalendarIcon, Stethoscope, CheckCircle2, X, ExternalLink, Video, MapPin } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const REASONS = [
  "General check-up",
  "Follow-up consult",
  "Prescription refill",
  "Test results",
  "New concern",
  "Other",
];

function formatDate(iso) {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-AU", {
      weekday: "short", day: "numeric", month: "short",
    });
  } catch { return iso; }
}

export default function MedicalBookingModal({ practice, open, onClose }) {
  const [step, setStep] = useState(1);
  const [doctorId, setDoctorId] = useState("walk-in");
  const [visitMode, setVisitMode] = useState("in_person"); // "in_person" | "telehealth"
  const [availability, setAvailability] = useState({ doctors: [], slots: [], calendly_url: "" });
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [pickedDate, setPickedDate] = useState("");
  const [pickedTime, setPickedTime] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", reason: REASONS[0], notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [calendlyOpen, setCalendlyOpen] = useState(false);

  useEffect(() => {
    if (!open || !practice?.slug) return;
    // Reset state on open
    setStep(1);
    setDoctorId("walk-in");
    setVisitMode("in_person");
    setPickedDate("");
    setPickedTime("");
    setConfirmation(null);
  }, [open, practice?.slug]);

  useEffect(() => {
    if (!open || !practice?.slug) return;
    setLoadingAvail(true);
    fetch(`${API}/api/directory/medical/${practice.slug}/availability?doctor_id=${encodeURIComponent(doctorId)}`)
      .then((r) => r.json())
      .then((d) => setAvailability({
        doctors: d.doctors || [],
        slots: d.slots || [],
        calendly_url: d.calendly_url || "",
      }))
      .catch(() => setAvailability({ doctors: [], slots: [], calendly_url: "" }))
      .finally(() => setLoadingAvail(false));
  }, [open, practice?.slug, doctorId]);

  const selectedDoctor = useMemo(
    () => availability.doctors.find((d) => d.id === doctorId) || availability.doctors[0],
    [availability.doctors, doctorId]
  );

  const dayBucket = useMemo(
    () => availability.slots.find((d) => d.date === pickedDate),
    [availability.slots, pickedDate]
  );

  async function submitBooking(calendlyEventUri = "") {
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/directory/restaurants/${practice.slug}/appointment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          doctor_id: doctorId,
          doctor_name: selectedDoctor?.name || "Walk-In Doctor",
          visit_mode: visitMode,
          date: pickedDate || new Date().toISOString().slice(0, 10),
          time: pickedTime || "Calendly",
          calendly_event_uri: calendlyEventUri,
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Could not book appointment");
      }
      const j = await r.json();
      setConfirmation(j.appointment);
      setStep(4);
      toast.success("Appointment confirmed");
    } catch (e) {
      toast.error(e.message || "Booking failed");
    } finally {
      setSubmitting(false);
    }
  }

  if (!practice) return null;
  const tpl = practice.medical_template || {};
  const accent = tpl.primary || "#0369a1";
  const surface = tpl.surface || "#f0f9ff";

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
        <DialogContent
          data-testid="medical-booking-modal"
          className="max-w-2xl p-0 overflow-hidden gap-0 max-h-[92vh] flex flex-col"
        >
          <DialogTitle className="sr-only">Book an appointment</DialogTitle>

          {/* Header */}
          <div
            className="px-6 py-5 text-white relative"
            style={{ background: `linear-gradient(135deg, ${accent} 0%, ${tpl.ink || "#0c4a6e"} 100%)` }}
          >
            <button
              onClick={() => onClose?.()}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 grid place-items-center"
              data-testid="booking-close"
            >
              <X className="w-4 h-4" />
            </button>
            <p className="text-[11px] uppercase tracking-[0.18em] opacity-90 font-bold flex items-center gap-1.5">
              <CalendarIcon className="w-3 h-3" /> Book an appointment
            </p>
            <h2 className="text-2xl font-bold mt-1.5">{practice.name}</h2>
            {tpl.lead && <p className="text-sm opacity-90 mt-1">{tpl.lead}</p>}
            <div className="mt-3 flex items-center gap-2 text-[11px]">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-1 rounded-full flex-1 transition-colors ${step >= s ? "bg-white" : "bg-white/30"}`}
                />
              ))}
            </div>
          </div>

          <div className="overflow-y-auto px-6 py-6 space-y-5">
            {/* STEP 1: Doctor */}
            {step === 1 && (
              <div data-testid="booking-step-doctor">
                {/* Telehealth / In-person toggle */}
                <h3 className="text-[11px] uppercase tracking-[0.18em] font-bold mb-2" style={{ color: accent }}>
                  How would you like to be seen?
                </h3>
                <div className="grid grid-cols-2 gap-2 mb-5" data-testid="visit-mode-toggle">
                  <button
                    data-testid="visit-mode-in_person"
                    onClick={() => setVisitMode("in_person")}
                    className={`p-3 rounded-xl border-2 transition-all flex items-center gap-2 text-left ${
                      visitMode === "in_person" ? "border-current shadow-sm" : "border-gray-200 hover:border-gray-300"
                    }`}
                    style={visitMode === "in_person" ? { borderColor: accent, background: surface } : {}}
                  >
                    <MapPin className="w-4 h-4 shrink-0" style={visitMode === "in_person" ? { color: accent } : { color: "#64748b" }} />
                    <div>
                      <p className="font-semibold text-[13px] text-gray-900">In-person</p>
                      <p className="text-[11px] text-gray-500">Visit the practice</p>
                    </div>
                  </button>
                  <button
                    data-testid="visit-mode-telehealth"
                    onClick={() => setVisitMode("telehealth")}
                    className={`p-3 rounded-xl border-2 transition-all flex items-center gap-2 text-left ${
                      visitMode === "telehealth" ? "border-current shadow-sm" : "border-gray-200 hover:border-gray-300"
                    }`}
                    style={visitMode === "telehealth" ? { borderColor: accent, background: surface } : {}}
                  >
                    <Video className="w-4 h-4 shrink-0" style={visitMode === "telehealth" ? { color: accent } : { color: "#64748b" }} />
                    <div>
                      <p className="font-semibold text-[13px] text-gray-900">Telehealth</p>
                      <p className="text-[11px] text-gray-500">Video consult from home</p>
                    </div>
                  </button>
                </div>

                <h3 className="text-[11px] uppercase tracking-[0.18em] font-bold mb-3" style={{ color: accent }}>
                  Step 1 · Choose a doctor
                </h3>
                <div className="space-y-2">
                  {availability.doctors.map((d) => (
                    <button
                      key={d.id}
                      data-testid={`doctor-option-${d.id}`}
                      onClick={() => setDoctorId(d.id)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                        doctorId === d.id ? "border-current shadow-sm" : "border-gray-200 hover:border-gray-300"
                      }`}
                      style={doctorId === d.id ? { borderColor: accent, background: surface } : {}}
                    >
                      <div
                        className="w-10 h-10 rounded-full grid place-items-center text-white text-sm font-bold"
                        style={{ background: d.is_walk_in ? "#475569" : accent }}
                      >
                        {d.is_walk_in ? "WI" : (d.name.match(/Dr\s+(\w)/)?.[1] || "D")}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[15px] text-gray-900">{d.name}</p>
                        <p className="text-[12px] text-gray-600">{d.specialty}</p>
                      </div>
                      {d.is_walk_in && (
                        <Badge className="text-[10px]" style={{ background: surface, color: accent, border: `1px solid ${accent}` }}>
                          Default
                        </Badge>
                      )}
                    </button>
                  ))}
                </div>
                <Button
                  data-testid="booking-step-1-next"
                  className="w-full mt-5 h-11 rounded-full text-white"
                  style={{ background: accent }}
                  onClick={() => setStep(2)}
                >
                  Continue to time
                </Button>
              </div>
            )}

            {/* STEP 2: Date + Time */}
            {step === 2 && (
              <div data-testid="booking-step-time">
                <h3 className="text-[11px] uppercase tracking-[0.18em] font-bold mb-3" style={{ color: accent }}>
                  Step 2 · Pick a date &amp; time
                </h3>
                <p className="text-[13px] text-gray-600 mb-3">
                  Showing the next 14 days for <strong>{selectedDoctor?.name}</strong>.
                </p>

                {/* Date pills */}
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 mb-4">
                  {loadingAvail && <p className="text-sm text-gray-500">Loading…</p>}
                  {availability.slots.map((day) => {
                    const active = pickedDate === day.date;
                    return (
                      <button
                        key={day.date}
                        data-testid={`booking-date-${day.date}`}
                        onClick={() => { setPickedDate(day.date); setPickedTime(""); }}
                        className={`shrink-0 px-3 py-2 rounded-xl border text-[12px] font-medium transition-all ${
                          active ? "text-white border-transparent" : "bg-white border-gray-200 text-gray-700 hover:border-gray-400"
                        }`}
                        style={active ? { background: accent } : {}}
                      >
                        {formatDate(day.date)}
                        <span className="block text-[10px] opacity-80 mt-0.5">{day.times.length} slots</span>
                      </button>
                    );
                  })}
                </div>

                {/* Time grid */}
                {pickedDate && dayBucket && (
                  <div className="grid grid-cols-4 gap-2" data-testid="booking-times-container">
                    {dayBucket.times.map((t) => {
                      const active = pickedTime === t;
                      return (
                        <button
                          key={t}
                          data-testid={`booking-time-${t}`}
                          onClick={() => setPickedTime(t)}
                          className={`h-10 rounded-lg border text-[13px] font-medium transition-all ${
                            active ? "text-white border-transparent" : "bg-white border-gray-200 text-gray-700 hover:border-gray-400"
                          }`}
                          style={active ? { background: accent } : {}}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                )}

                {availability.calendly_url && (
                  <button
                    data-testid="booking-open-calendly"
                    onClick={() => setCalendlyOpen(true)}
                    className="mt-4 w-full p-3 rounded-xl border border-dashed text-[13px] text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Or schedule via Calendly
                  </button>
                )}

                <div className="flex gap-2 mt-5">
                  <Button variant="outline" className="flex-1 rounded-full" onClick={() => setStep(1)}>Back</Button>
                  <Button
                    data-testid="booking-step-2-next"
                    className="flex-1 rounded-full text-white"
                    style={{ background: accent }}
                    disabled={!pickedDate || !pickedTime}
                    onClick={() => setStep(3)}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 3: Patient form */}
            {step === 3 && (
              <div data-testid="booking-step-form">
                <h3 className="text-[11px] uppercase tracking-[0.18em] font-bold mb-3" style={{ color: accent }}>
                  Step 3 · Your details
                </h3>
                <div className="rounded-xl p-3 mb-4 flex items-center gap-3 text-[13px]" style={{ background: surface }}>
                  {visitMode === "telehealth" ? (
                    <Video className="w-4 h-4 shrink-0" style={{ color: accent }} />
                  ) : (
                    <Stethoscope className="w-4 h-4 shrink-0" style={{ color: accent }} />
                  )}
                  <span>
                    <strong>{selectedDoctor?.name}</strong>{" "}
                    {visitMode === "telehealth" ? "(Video consult)" : ""} · {formatDate(pickedDate)} · {pickedTime}
                  </span>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-[12px] font-semibold">Full name</Label>
                    <Input
                      data-testid="booking-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Jane Citizen"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[12px] font-semibold">Phone</Label>
                      <Input
                        data-testid="booking-phone"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="04xx xxx xxx"
                      />
                    </div>
                    <div>
                      <Label className="text-[12px] font-semibold">Email</Label>
                      <Input
                        data-testid="booking-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="jane@example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[12px] font-semibold">Reason for visit</Label>
                    <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                      <SelectTrigger data-testid="booking-reason"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[12px] font-semibold">Anything we should know? (optional)</Label>
                    <Textarea
                      data-testid="booking-notes"
                      rows={2}
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Allergies, symptoms, accessibility needs…"
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-5">
                  <Button variant="outline" className="flex-1 rounded-full" onClick={() => setStep(2)}>Back</Button>
                  <Button
                    data-testid="booking-submit"
                    className="flex-1 rounded-full text-white"
                    style={{ background: accent }}
                    disabled={!form.name || (!form.phone && !form.email) || submitting}
                    onClick={() => submitBooking()}
                  >
                    {submitting ? "Booking…" : "Confirm appointment"}
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 4: Confirmation */}
            {step === 4 && confirmation && (
              <div data-testid="booking-confirmation" className="text-center py-4">
                <div
                  className="w-16 h-16 rounded-full grid place-items-center mx-auto mb-4"
                  style={{ background: surface }}
                >
                  <CheckCircle2 className="w-8 h-8" style={{ color: accent }} strokeWidth={2.5} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">You're booked in</h3>
                <p className="text-[14px] text-gray-600 mt-1">
                  Reference <span className="font-mono font-bold">{confirmation.id}</span>
                </p>
                <div className="mt-5 rounded-xl p-4 text-left text-[13px] space-y-1.5" style={{ background: surface }}>
                  <p><strong>Practice:</strong> {confirmation.practice_name}</p>
                  <p><strong>Doctor:</strong> {confirmation.doctor_name}</p>
                  <p><strong>When:</strong> {formatDate(confirmation.date)} at {confirmation.time}</p>
                  <p><strong>For:</strong> {form.reason}</p>
                </div>
                <p className="text-[12px] text-gray-500 mt-4">
                  A confirmation will be sent to {form.email || form.phone}.
                </p>
                <Button
                  data-testid="booking-done"
                  className="mt-5 rounded-full text-white px-8"
                  style={{ background: accent }}
                  onClick={() => onClose?.()}
                >
                  Done
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Calendly popup (when admin has configured a URL) */}
      {availability.calendly_url && (
        <PopupModal
          url={availability.calendly_url}
          open={calendlyOpen}
          onModalClose={() => setCalendlyOpen(false)}
          rootElement={document.getElementById("root") || document.body}
          prefill={{ name: form.name, email: form.email }}
          pageSettings={{ hideEventTypeDetails: false, hideLandingPageDetails: false }}
        />
      )}
    </>
  );
}
